import type { AxisSelection } from '@atlas/types'
import { ListSkeleton, toggleFilterValue } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import {
	DndContext,
	type DragEndEvent,
	DragOverlay,
	type DragStartEvent,
	KeyboardSensor,
	MeasuringStrategy,
	PointerSensor,
	pointerWithin,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import { Plus, SlidersHorizontal } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/editorial/PageHeader'
import { ScopeToggle } from '@/components/work-orders/ScopeToggle'
import { DONE_VISIBLE_DAYS } from '@/config/board'
import { setLaneScreen } from '@/domain/checklist-stub'
import type { BoardWorkflow, WorkOrder } from '@/domain/work-order.types'
import { isUnderReview, isVisibleByDefault, mayWithdraw, statusesOf } from '@/domain/work-order.types'
import { resolveLane } from '@/domain/workflow-stub'
import { useAuth } from '@/hooks/useAuth'
import { useTransitionWorkOrder, useWorkOrderBoard } from '@/hooks/useWorkOrders'
import { apiErrorMessage } from '@/lib/api-error'
import { useCan } from '@/lib/capabilities'
import { REFUSAL } from '@/lib/refusal'
import { notification } from '@/notifications/notification-store'
import { BoardColumns, DragCard, parseDropId } from './BoardColumns'
import { ActiveFilterChips, BoardFilters, matchesSelection } from './BoardFilters'
import { buildBoardModel } from './board-model'

type Scope = 'mine' | 'all'

const SCOPE_OPTIONS: ReadonlyArray<{ value: Scope; label: string }> = [
	{ value: 'mine', label: 'My orders' },
	{ value: 'all', label: 'All' },
]

// dnd-kit re-measures column rects continuously — the board scrolls horizontally,
// so rects shift as columns grow/shrink; MeasuringStrategy.Always keeps them fresh.
const MEASURING = { droppable: { strategy: MeasuringStrategy.Always } }

/** Header action cluster: scope toggle · filter · new order. */
function BoardActions({
	scope,
	onScope,
	onToggleFilters,
	onNew,
}: {
	scope: Scope
	onScope: (s: Scope) => void
	onToggleFilters: () => void
	onNew: () => void
}) {
	return (
		<>
			<ScopeToggle label="Show orders" options={SCOPE_OPTIONS} value={scope} onChange={onScope} />
			<Button variant="secondary" className="gap-1.5 rounded-ds-full" onPress={onToggleFilters}>
				<SlidersHorizontal className="h-4 w-4" />
				Filter
			</Button>
			<Button onPress={onNew} className="gap-1.5 whitespace-nowrap rounded-ds-full px-5">
				<Plus className="h-4 w-4" />
				New work order
			</Button>
		</>
	)
}

/** Count line + the "show/hide long-done" toggle under the header. */
function BoardSummary({
	count,
	hiddenDone,
	showOldDone,
	onToggle,
}: {
	count: number
	hiddenDone: number
	showOldDone: boolean
	onToggle: () => void
}) {
	return (
		<p className="-mt-4 mb-4 flex shrink-0 flex-wrap items-center gap-x-2 font-mono text-xs text-ds-text-subtle">
			<span>
				{count} order{count === 1 ? '' : 's'} · drag a card between columns to change its status
			</span>
			{hiddenDone > 0 ? (
				<button
					type="button"
					onClick={onToggle}
					className="underline decoration-dotted underline-offset-2 hover:text-ds-text"
				>
					{showOldDone
						? `hide ${hiddenDone} done > ${DONE_VISIBLE_DAYS}d`
						: `+ ${hiddenDone} done > ${DONE_VISIBLE_DAYS}d hidden`}
				</button>
			) : null}
		</p>
	)
}

/**
 * The transition a drop represents, or `null` when the drop is not a move: no
 * drop target, no dragged card, a target in a different workflow (a status code
 * is only meaningful inside the workflow that defines it, and the server would
 * reject it), or the card's own current status.
 */
function resolveDrop(wo: WorkOrder | undefined, dropTargetId: string | null): { wo: WorkOrder; status: string } | null {
	if (!wo || !dropTargetId) return null
	const { workflowId, status } = parseDropId(dropTargetId)
	if (wo.workflowId !== workflowId) return null
	if (wo.status === status) return null
	return { wo, status }
}

/**
 * A fresh review screen every time a move lands the order on a validation lane
 * — mirrors the action bar's own reset so a lane always starts on review
 * regardless of whether the drag or a CTA moved it there.
 */
function resetLaneScreen(workOrderId: string, status: string) {
	const destLane = resolveLane(status)
	if (destLane) setLaneScreen(workOrderId, destLane, 'review')
}

/**
 * Work-order board (route `/board`). Cards drag between the configured status
 * columns of their own workflow to change status. Moves are optimistic via the
 * transition endpoint; the filter rail cuts by axis tuple; my-orders scopes to
 * the caller.
 */
/**
 * Dragging a card is the board's other way to transition, so it answers the same
 * permission question the action bar does — and it is not a control `Restricted`
 * can wrap. Lifted out of the page because the page has a line budget and this is
 * a self-contained unit, not because either half is reused.
 */
function useBoardDrag({
	canWork,
	canConfigure,
	userId,
	workflows,
	transition,
	setDragging,
}: {
	canWork: boolean
	/** Moving an order OFF a validation lane is a reviewer action, ADMIN and above. */
	canConfigure: boolean
	/** An assignee may still withdraw their own order from the first review. */
	userId: string | undefined
	workflows: BoardWorkflow[]
	transition: ReturnType<typeof useTransitionWorkOrder>
	setDragging: (wo: WorkOrder | null) => void
}) {
	return {
		onDragStart: (e: DragStartEvent) => setDragging((e.active.data.current?.wo as WorkOrder) ?? null),
		onDragEnd: ({ active, over }: DragEndEvent) => {
			setDragging(null)
			const move = resolveDrop(active.data.current?.wo as WorkOrder | undefined, over ? String(over.id) : null)
			if (!move) return
			const { wo, status } = move
			// Refused rather than prevented: the card snaps back on its own, so without
			// this the drag would look like it simply did not take.
			if (!canWork) {
				notification.info(REFUSAL.workOrder)
				return
			}
			if (
				isUnderReview(wo, workflows) &&
				!canConfigure &&
				!mayWithdraw(wo, status, statusesOf(wo, workflows), userId)
			) {
				notification.info(REFUSAL.review)
				return
			}
			transition.mutate(
				{ id: wo.workOrderId, toStatus: status },
				{
					onSuccess: () => resetLaneScreen(wo.workOrderId, status),
					// There is no dedicated messaging slot on the board, so a rejected move
					// surfaces as a notification; the optimistic card snaps back on its own.
					onError: (error) => notification.error(apiErrorMessage(error, "What's missing?")),
				},
			)
		},
	}
}

export function BoardPage() {
	const { user } = useAuth()
	const navigate = useNavigate()
	const [scope, setScope] = useState<Scope>('all')
	const [showFilters, setShowFilters] = useState(false)
	const [shownAxes, setShownAxes] = useState<string[]>(['brand', 'geography', 'species'])
	const [selection, setSelection] = useState<AxisSelection>({})
	const [dragging, setDragging] = useState<WorkOrder | null>(null)
	// Default view hides Done cards untouched for >3 days; toggle to reveal them.
	const [showOldDone, setShowOldDone] = useState(false)

	const assigneeId = scope === 'mine' ? (user?.userId ?? undefined) : undefined
	const {
		data: page,
		isLoading,
		isError,
		refetch,
	} = useWorkOrderBoard(assigneeId ? { 'filters[assignee]': assigneeId } : undefined)
	const orders = page?.workOrders ?? []
	const transition = useTransitionWorkOrder()
	const { canWork, canConfigure } = useCan()

	// A drag from a click is filtered out by the 8px activation constraint (below),
	// so plain clicks still open the detail.
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
		useSensor(KeyboardSensor),
	)

	const onOpen = (id: string) => void navigate(`/work-orders/${id}`)
	const toggleValue = (axisCode: string, valueCode: string) =>
		setSelection((s) => toggleFilterValue(s, axisCode, valueCode))

	// Axis filters cut client-side by the order's tuple (backend search delegation
	// can move this server-side later); status/assignee come from the query. The
	// default view also drops long-done cards unless the user opts to show them.
	const workflows = page?.workflows ?? []

	const { onDragStart, onDragEnd } = useBoardDrag({
		canWork,
		canConfigure,
		userId: user?.userId,
		workflows,
		transition,
		setDragging,
	})
	const hiddenDone = orders.filter((w) => !isVisibleByDefault(w, workflows)).length
	const visible = orders.filter(
		(w) => matchesSelection(w.tuple, selection) && (showOldDone || isVisibleByDefault(w, workflows)),
	)

	return (
		<div className="flex min-h-0 min-w-0 flex-1 flex-col">
			<PageHeader
				className="mb-8 shrink-0"
				eyebrow="Work Orders"
				title="Board"
				actions={
					<BoardActions
						scope={scope}
						onScope={setScope}
						onToggleFilters={() => setShowFilters((v) => !v)}
						onNew={() => void navigate('/work-orders/new')}
					/>
				}
			/>
			<BoardSummary
				count={visible.length}
				hiddenDone={hiddenDone}
				showOldDone={showOldDone}
				onToggle={() => setShowOldDone((v) => !v)}
			/>

			<ActiveFilterChips selection={selection} onRemove={toggleValue} onClear={() => setSelection({})} />

			{isError ? (
				<div className="rounded-ds-md border border-ds-border p-6 text-center">
					<p className="text-ds-text-muted">Couldn’t load work orders.</p>
					<Button variant="secondary" className="mt-3" onPress={() => void refetch()}>
						Retry
					</Button>
				</div>
			) : isLoading ? (
				<ListSkeleton label="Loading…" />
			) : (
				<div className="flex min-h-0 min-w-0 flex-1 gap-5">
					{showFilters ? (
						<BoardFilters
							shownAxisCodes={shownAxes}
							selection={selection}
							onToggle={toggleValue}
							onAddAxis={(code) => setShownAxes((a) => (a.includes(code) ? a : [...a, code]))}
						/>
					) : null}
					<div className="flex min-h-0 min-w-0 flex-1 flex-col">
						<DndContext
							sensors={sensors}
							collisionDetection={pointerWithin}
							measuring={MEASURING}
							onDragStart={onDragStart}
							onDragEnd={onDragEnd}
							onDragCancel={() => setDragging(null)}
						>
							<BoardColumns model={buildBoardModel(visible, workflows)} onOpen={onOpen} workflows={workflows} />
							<DragOverlay dropAnimation={null}>
								{dragging ? <DragCard wo={dragging} workflows={workflows} /> : null}
							</DragOverlay>
						</DndContext>
					</div>
				</div>
			)}
		</div>
	)
}
